import React from 'react';
import { useTranslation } from 'react-i18next';
import { IValidationResults } from '@/data/models/validation';
import ErrorAlert from '../../../common/ErrorAlert/ErrorAlert';
import IsrcMetadataConflictDetails from './IsrcMetadataConflictDetails';
import './ValidationErrors.scss';
import {
  isValidationError,
  isValidationWarning,
} from '@/utils/validationErrors';

interface Props {
  errorBanner: boolean;
  errors: string[];
  warningMessages?: string[];
  validationErrors?: IValidationResults[];
  catalogUpc?: string | null;
  region: string;
}

const ValidationErrors = ({
  errorBanner,
  errors,
  warningMessages = [],
  validationErrors = [],
  catalogUpc,
  region,
}: Props) => {
  const { t } = useTranslation();
  const errorValidationResults = validationErrors.filter(isValidationError);
  const warningValidationResults = validationErrors.filter(isValidationWarning);

  const buildSummaryMessages = (
    messages: string[],
    validationResults: IValidationResults[]
  ) =>
    messages.length > 0
      ? messages
      : Array.from(
          new Set(
            validationResults
              .map((error) => error.validationMessage)
              .filter((message): message is string => !!message)
          )
        );

  const buildContent = (
    messages: string[],
    validationResults: IValidationResults[]
  ) => {
    const summaryMessages = buildSummaryMessages(messages, validationResults);

    return (
      <div className="validation-errors-content">
        {summaryMessages.length > 0 ? (
          <ul className="validation-errors-content__summary-list">
            {summaryMessages.map((error, index) => (
              <li key={`${error}-${index}`}>{error}</li>
            ))}
          </ul>
        ) : null}
        <IsrcMetadataConflictDetails
          errors={validationResults}
          fallbackCatalogUpc={catalogUpc}
        />
      </div>
    );
  };

  const renderAlert = (
    variant: 'danger' | 'warning',
    showBanner: boolean,
    messages: string[],
    validationResults: IValidationResults[]
  ) => {
    if (
      !showBanner ||
      (messages.length === 0 && validationResults.length === 0)
    ) {
      return null;
    }

    const content = buildContent(messages, validationResults);
    const titleText =
      variant === 'warning'
        ? t('product.editProduct.metadataWarning')
        : t('validationAlert.productEditView.headerMessage');

    if (region === 'View') {
      return (
        <ErrorAlert
          contentText={content}
          titleText={titleText}
          dismissable={false}
          variant={variant}
        />
      );
    }

    if (region === 'Edit') {
      return (
        <ErrorAlert
          contentText={content}
          dismissable={false}
          variant={variant}
        />
      );
    }

    return null;
  };

  return (
    <div>
      {renderAlert('danger', errorBanner, errors, errorValidationResults)}
      {renderAlert(
        'warning',
        errorBanner,
        warningMessages,
        warningValidationResults
      )}
    </div>
  );
};

export default ValidationErrors;
